import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './Dashboard.css';

const API_BASE_URL = '/api';

const Dashboard = ({ user }) => {
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    activeToday: 0,
    dau: 0,
    mau: 0,
    newThisWeek: 0,
    totalChats: 0,
    messagesToday: 0,
    avgSessionDuration: 0,
    feedbackRating: 0,
    trends: { users: 0, chats: 0, messages: 0, rating: 0 }
  });
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [chatActivityData, setChatActivityData] = useState([]);
  const [activeUsersData, setActiveUsersData] = useState([]);
  const [userDistribution, setUserDistribution] = useState({ byType: {}, byCountry: {} });
  const [engagementMetrics, setEngagementMetrics] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [metricsRes, growthRes, activityRes, activeUsersRes, distributionRes, engagementRes, recentRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/analytics/metrics`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/admin/analytics/user-growth`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/admin/analytics/chat-activity`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/admin/analytics/active-users`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/admin/analytics/distribution`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/admin/analytics/engagement`, { withCredentials: true }),
        axios.get(`${API_BASE_URL}/admin/analytics/recent-activity`, { withCredentials: true })
      ]);

      if (metricsRes.data.success) setMetrics(metricsRes.data.data);
      if (growthRes.data.success) setUserGrowthData(growthRes.data.data);
      if (activityRes.data.success) setChatActivityData(activityRes.data.data);
      if (activeUsersRes.data.success) setActiveUsersData(activeUsersRes.data.data);
      if (distributionRes.data.success) setUserDistribution(distributionRes.data.data);
      if (engagementRes.data.success) setEngagementMetrics(engagementRes.data.data);
      if (recentRes.data.success) setRecentActivity(recentRes.data.data);

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchAllData();
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  const renderTrend = (value) => {
    if (value > 0) return <span className="trend-up">+{value}% ↑</span>;
    if (value < 0) return <span className="trend-down">{value}% ↓</span>;
    return <span className="trend-neutral">{value}%</span>;
  };

  // Prepare pie chart data
  const typeChartData = [
    { name: 'Doctors', value: userDistribution.byType.doctors || 0, color: '#667eea' },
    { name: 'Students', value: userDistribution.byType.students || 0, color: '#764ba2' }
  ];

  const countryChartData = Object.entries(userDistribution.byCountry || {}).map(([name, value], index) => ({
    name,
    value,
    color: ['#667eea', '#764ba2', '#f093fb', '#4facfe'][index % 4]
  }));

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1 className="page-title">Admin Dashboard</h1>
          <p className="page-description">Welcome {user?.name} - Real-time comprehensive metrics and insights</p>
        </div>
        <div className="header-actions">
          <div className="auto-refresh-toggle">
            <label>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh (10s)
            </label>
          </div>
          <div className="last-updated">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </div>
        </div>
      </div>

      {loading && <div className="loading-indicator">Refreshing...</div>}

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-label">Total Users</div>
          <div className="metric-value">{metrics.totalUsers}</div>
          <div className="metric-trend">{renderTrend(metrics.trends.users)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Active Today</div>
          <div className="metric-value">{metrics.activeToday}</div>
          <div className="metric-badge">Live</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Daily Active Users</div>
          <div className="metric-value">{metrics.dau}</div>
          <div className="metric-trend">DAU</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Monthly Active Users</div>
          <div className="metric-value">{metrics.mau}</div>
          <div className="metric-trend">MAU (Last 30 days)</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">New This Week</div>
          <div className="metric-value">+{metrics.newThisWeek}</div>
          <div className="metric-trend">New signups</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Total Chats</div>
          <div className="metric-value">{metrics.totalChats}</div>
          <div className="metric-trend">{renderTrend(metrics.trends.chats)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Messages Today</div>
          <div className="metric-value">{metrics.messagesToday}</div>
          <div className="metric-trend">{renderTrend(metrics.trends.messages)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Avg Session</div>
          <div className="metric-value">{formatDuration(metrics.avgSessionDuration)}</div>
          <div className="metric-trend">{renderTrend(metrics.trends.sessions)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Feedback Rating</div>
          <div className="metric-value">{metrics.feedbackRating} ⭐</div>
          <div className="metric-trend">{renderTrend(metrics.trends.rating)}</div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-row">
        {/* User Growth Chart */}
        <div className="chart-card full-width">
          <h3 className="chart-title">User Growth (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={userGrowthData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#667eea" strokeWidth={2} name="Total Users" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* DAU/MAU Chart */}
      <div className="charts-row">
        <div className="chart-card full-width">
          <h3 className="chart-title">Daily Active Users (DAU) & Monthly Active Users (MAU)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={activeUsersData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="dau" stroke="#10b981" strokeWidth={2} name="Daily Active Users (DAU)" />
              <Line type="monotone" dataKey="mau" stroke="#3b82f6" strokeWidth={2} name="Monthly Active Users (MAU)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Distribution & Activity Charts */}
      <div className="charts-row">
        {/* User Distribution by Type */}
        <div className="chart-card">
          <h3 className="chart-title">User Distribution by Type</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={typeChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {typeChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* User Distribution by Country */}
        <div className="chart-card">
          <h3 className="chart-title">User Distribution by Country</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={countryChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {countryChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Chat Activity Chart */}
      <div className="charts-row">
        <div className="chart-card full-width">
          <h3 className="chart-title">Chat Activity (Last 7 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chatActivityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="messages" fill="#667eea" name="Messages" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="engagement-section">
        <h3 className="section-title">Engagement Metrics</h3>
        <div className="engagement-grid">
          <div className="engagement-item">
            <div className="engagement-label">Avg Messages per Chat</div>
            <div className="engagement-value">{engagementMetrics.avgMessagesPerChat || 0}</div>
          </div>
          <div className="engagement-item">
            <div className="engagement-label">Most Active Hour</div>
            <div className="engagement-value">{engagementMetrics.mostActiveHour || 'N/A'}</div>
          </div>
          <div className="engagement-item">
            <div className="engagement-label">Bounce Rate</div>
            <div className="engagement-value">{engagementMetrics.bounceRate || 0}%</div>
          </div>
          <div className="engagement-item">
            <div className="engagement-label">Return User Rate</div>
            <div className="engagement-value">{engagementMetrics.returnUserRate || 0}%</div>
          </div>
          <div className="engagement-item">
            <div className="engagement-label">Avg Response Time</div>
            <div className="engagement-value">{engagementMetrics.avgResponseTime || 0}s</div>
          </div>
          <div className="engagement-item">
            <div className="engagement-label">Error Rate</div>
            <div className="engagement-value">{engagementMetrics.errorRate || 0}%</div>
          </div>
        </div>
      </div>

      {/* Recent Activity Feed */}
      <div className="activity-section">
        <div className="activity-header">
          <h3 className="section-title">Recent Activity</h3>
          <button className="refresh-btn" onClick={fetchAllData}>Refresh</button>
        </div>
        <div className="activity-feed">
          {recentActivity.length === 0 ? (
            <div className="activity-empty">No recent activity</div>
          ) : (
            recentActivity.map((activity) => (
              <div key={activity.id} className={`activity-item activity-${activity.type}`}>
                <div className="activity-icon">
                  {activity.type === 'signup' && '👤'}
                  {activity.type === 'chat' && '💬'}
                  {activity.type === 'feedback' && '⭐'}
                </div>
                <div className="activity-details">
                  <div className="activity-description">{activity.description}</div>
                  <div className="activity-time">{formatTime(activity.timestamp)}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
